/**
 * Helpers for files the API serves out of its own upload/asset directories.
 */

/**
 * Uploaded files are stored as server-relative paths ("/uploads/avatars/x.png")
 * and served by the API, which is a different origin from the client in
 * development (:7000 vs :7002). Resolving against the API origin is what makes
 * the image actually load.
 */
export const resolveAssetUrl = (assetPath?: string | null): string => {
  if (!assetPath) return '';
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  const apiBase = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7002/api';
  const origin = String(apiBase).replace(/\/api\/?$/, '');
  return `${origin}${assetPath.startsWith('/') ? '' : '/'}${assetPath}`;
};

/**
 * Broadcast when the signed-in user changes their avatar.
 *
 * The sidebar and the settings page each hold their own copy of the profile,
 * and the stored session user has no avatar field at all. Rather than add a
 * global store or make the sidebar poll, the uploader announces the new path
 * and anyone showing it updates in place -- no refetch, no reload.
 */
export const AVATAR_UPDATED_EVENT = 'profile:avatar-updated';

export const announceAvatarChange = (avatarPath: string): void => {
  window.dispatchEvent(new CustomEvent(AVATAR_UPDATED_EVENT, { detail: avatarPath }));
};

/**
 * Absolute API base ("http://host:7002/api").
 *
 * A few call sites use raw `fetch('/api/...')`, which resolves against the
 * CLIENT origin. That only works behind a proxy; in development the client is
 * on :7000 and the API on :7002, so those requests 404. Use this instead.
 */
export const apiBaseUrl = (): string =>
  String((import.meta as any).env?.VITE_API_URL || 'http://localhost:7002/api').replace(/\/$/, '');

/** Join the API base with a path, with or without a leading slash. */
export const apiUrl = (path: string): string =>
  `${apiBaseUrl()}/${String(path).replace(/^\/+/, '')}`;
